import React, { useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import './BonusItemsPopup.css';

/* ─── Data ─────────────────────────────────── */
const BONUS_ITEMS = [
  {
    id: 'free-bag',
    name: 'Premium Laptop Bag',
    image: '/images/bonus/bag.jpg',
    fallback: '/images/trending-image.jpeg',
    originalPrice: 999,
    description: 'Padded protection · shoulder strap',
  },
  {
    id: 'free-antivirus',
    name: 'Quick Heal Total Security',
    image: '/images/bonus/quickheal.webp',
    fallback: '/images/trending-image.jpeg',
    originalPrice: 1499,
    description: '1-year license · all devices',
  },
];

const TOTAL_VALUE = BONUS_ITEMS.reduce((s, i) => s + i.originalPrice, 0);
const fmt = (n) => `₹${n.toLocaleString('en-IN')}`;

/* ─── Icons ─────────────────────────────────── */
const ArrowRight = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className="nw-cta-arrow" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const CloseIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ─── Component ─────────────────────────────── */
const BonusItemsPopup = ({ onClose }) => {
  const overlayRef = useRef(null);
  const popupRef   = useRef(null);
  const itemsRef   = useRef([]);
  const tlRef      = useRef(null);

  /* ── Entrance ─────────────────────────────── */
  useEffect(() => {
    const overlay = overlayRef.current;
    const popup   = popupRef.current;
    const items   = itemsRef.current.filter(Boolean);
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    overlay.classList.add('is-active');

    if (prefersReduced) {
      gsap.set(overlay, { opacity: 1 });
      gsap.set(popup, { opacity: 1, y: 0, scale: 1 });
      return;
    }

    const isMobile = window.innerWidth < 640;

    tlRef.current = gsap.timeline({ defaults: { ease: 'expo.out' } });

    tlRef.current
      .to(overlay, { opacity: 1, duration: 0.3 })
      .fromTo(popup,
        { opacity: 0, y: isMobile ? 50 : 24, scale: isMobile ? 1 : 0.98 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'expo.out' },
        '-=0.15'
      )
      .fromTo(items,
        { opacity: 0, x: -8 },
        { opacity: 1, x: 0, duration: 0.35, stagger: 0.05, ease: 'power3.out' },
        '-=0.25'
      );

    return () => { tlRef.current?.kill(); };
  }, []);

  /* ── Exit ─────────────────────────────────── */
  const animateOut = useCallback((cb) => {
    const overlay = overlayRef.current;
    const popup   = popupRef.current;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) { cb?.(); return; }

    gsap.timeline({ onComplete: cb })
      .to(popup, { opacity: 0, y: 12, scale: 0.98, duration: 0.2, ease: 'power2.in' })
      .to(overlay, { opacity: 0, duration: 0.18, ease: 'power1.in' }, '-=0.1');
  }, []);

  const handleClose = useCallback(() => animateOut(() => onClose?.()), [animateOut, onClose]);

  const handleShopNow = useCallback(() => {
    animateOut(() => {
      onClose?.();
      window.location.href = '/laptops';
    });
  }, [animateOut, onClose]);

  /* ── Keyboard ─────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  const handleOverlayClick = useCallback(
    (e) => { if (e.target === overlayRef.current) handleClose(); },
    [handleClose]
  );

  /* ── Focus trap ───────────────────────────── */
  useEffect(() => {
    const popup = popupRef.current;
    const focusable = popup.querySelectorAll(
      'button, [href], input, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    first?.focus();

    const trap = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };

    popup.addEventListener('keydown', trap);
    return () => popup.removeEventListener('keydown', trap);
  }, []);

  /* ── Render ───────────────────────────────── */
  return (
    <div
      className="nw-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="nw-popup-title"
    >
      <div className="nw-popup" ref={popupRef}>

        {/* Close */}
        <button className="nw-close" onClick={handleClose} aria-label="Close">
          <CloseIcon size={18} />
        </button>

        {/* Header */}
        <header className="nw-header">
          <p className="nw-kicker">Bundle offer</p>
          <h2 className="nw-headline" id="nw-popup-title">
            Get Gifts worth <em>{fmt(TOTAL_VALUE)}</em><br /> Absolutely FREE!
          </h2>
        </header>

        {/* Items */}
        <ul className="nw-items" aria-label="Included bonus items">
          {BONUS_ITEMS.map((item, i) => (
            <li
              key={item.id}
              className="nw-item"
              ref={(el) => (itemsRef.current[i] = el)}
            >
              <img
                className="nw-item-img"
                src={item.image}
                alt=""
                width={40}
                height={40}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = item.fallback;
                }}
              />

              <div className="nw-item-body">
                <h3 className="nw-item-name">{item.name}</h3>
                <p className="nw-item-desc">{item.description}</p>
              </div>

              <div className="nw-item-price">
                <span className="nw-item-was">{fmt(item.originalPrice)}</span>
                <span className="nw-item-now">Included</span>
              </div>
            </li>
          ))}
        </ul>

        {/* Footer with CTA */}
        <footer className="nw-footer">
          <div className="nw-footer-meta">
            <span className="nw-footer-label">Added at checkout with any laptop purchase</span>
          </div>

          <button
            className="nw-cta"
            onClick={handleShopNow}
            aria-label="Browse laptops"
          >
            <span>Browse laptops</span>
            <ArrowRight size={16} />
          </button>

          <button className="nw-decline" onClick={handleClose}>
            Not now
          </button>
        </footer>
      </div>
    </div>
  );
};

export default BonusItemsPopup;